/**
 * Admin Partnerships Tab
 * Manage brand partnership applications
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Handshake, RefreshCw, CheckCircle, XCircle, Clock, Mail, Globe, Building2,
  ChevronLeft, ChevronRight, MessageSquare, Eye
} from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

import { useAuth } from "../../contexts/AuthContext";
import { API } from "../../lib/api";

const AdminPartnershipsTab = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, limit: 20, skip: 0 });
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedApp, setSelectedApp] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [adminNotes, setAdminNotes] = useState("");

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: pagination.limit.toString(),
        skip: pagination.skip.toString()
      });
      if (statusFilter) params.append("status", statusFilter);

      const res = await axios.get(`${API}/partnerships?${params}`, {
        withCredentials: true
      });
      setApplications(res.data.applications || []);
      setPagination(prev => ({ ...prev, total: res.data.total || 0 }));
    } catch (err) {
      toast.error("Failed to load partnership applications");
    } finally {
      setLoading(false);
    }
  }, [pagination.limit, pagination.skip, statusFilter]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const handleStatusUpdate = async (status) => {
    if (!selectedApp) return;
    setUpdating(true);
    try {
      await axios.put(
        `${API}/partnerships/${selectedApp.application_id}/status`,
        { status, admin_notes: adminNotes },
        { withCredentials: true }
      );
      toast.success(`Application ${status}`);
      setSelectedApp(null);
      setAdminNotes("");
      fetchApplications();
    } catch (err) {
      toast.error("Failed to update application");
    } finally {
      setUpdating(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-100 text-yellow-700",
      approved: "bg-green-100 text-green-700",
      rejected: "bg-red-100 text-red-700",
      contacted: "bg-blue-100 text-blue-700"
    };
    const icons = {
      pending: <Clock className="w-3 h-3 mr-1" />,
      approved: <CheckCircle className="w-3 h-3 mr-1" />,
      rejected: <XCircle className="w-3 h-3 mr-1" />,
      contacted: <Mail className="w-3 h-3 mr-1" />
    };
    return (
      <Badge className={`${styles[status] || 'bg-stone-100'} flex items-center w-fit`}>
        {icons[status]} {status}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit", month: "short", year: "numeric"
    });
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const currentPage = Math.floor(pagination.skip / pagination.limit) + 1;

  return (
    <div className="space-y-6" data-testid="admin-partnerships-tab">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Handshake className="w-5 h-5 text-amber-600" />
                Partnership Applications
              </CardTitle>
              <CardDescription>Review brand partnership requests</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={fetchApplications} disabled={loading}>
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Stats */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <Card className="bg-stone-50">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold">{pagination.total}</p>
                <p className="text-xs text-stone-500">Total</p>
              </CardContent>
            </Card>
            <Card className="bg-yellow-50">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-yellow-700">
                  {applications.filter(a => a.status === 'pending').length}
                </p>
                <p className="text-xs text-yellow-600">Pending</p>
              </CardContent>
            </Card>
            <Card className="bg-green-50">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-green-700">
                  {applications.filter(a => a.status === 'approved').length}
                </p>
                <p className="text-xs text-green-600">Approved</p>
              </CardContent>
            </Card>
            <Card className="bg-blue-50">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-blue-700">
                  {applications.filter(a => a.status === 'contacted').length}
                </p>
                <p className="text-xs text-blue-600">Contacted</p>
              </CardContent>
            </Card>
          </div>

          {/* Table */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600" />
            </div>
          ) : applications.length === 0 ? (
            <div className="text-center py-12">
              <Handshake className="w-16 h-16 text-stone-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-stone-700 mb-2">No Applications</h3>
              <p className="text-stone-500">Partnership applications will appear here</p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-stone-50">
                    <TableHead>Company</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {applications.map((app) => (
                    <TableRow key={app.application_id} className="hover:bg-stone-50">
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-stone-400" />
                          <span className="font-medium">{app.company_name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <p>{app.contact_name}</p>
                          <p className="text-stone-500">{app.contact_email}</p>
                        </div>
                      </TableCell>
                      <TableCell>{app.product_category}</TableCell>
                      <TableCell>{getStatusBadge(app.status)}</TableCell>
                      <TableCell className="text-stone-500 text-sm">{formatDate(app.created_at)}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setSelectedApp(app); setAdminNotes(app.admin_notes || ""); }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <p className="text-sm text-stone-600">Page {currentPage} of {totalPages}</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={currentPage === 1}
                  onClick={() => setPagination(p => ({ ...p, skip: p.skip - p.limit }))}>
                  <ChevronLeft className="w-4 h-4" /> Prev
                </Button>
                <Button variant="outline" size="sm" disabled={currentPage === totalPages}
                  onClick={() => setPagination(p => ({ ...p, skip: p.skip + p.limit }))}>
                  Next <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Application Detail Modal */}
      <Dialog open={!!selectedApp} onOpenChange={() => setSelectedApp(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-amber-600" />
              {selectedApp?.company_name}
            </DialogTitle>
            <DialogDescription>Partnership application details</DialogDescription>
          </DialogHeader>

          {selectedApp && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-stone-500 uppercase">Contact</p>
                  <p className="font-medium">{selectedApp.contact_name}</p>
                  <p className="text-sm text-stone-600">{selectedApp.contact_email}</p>
                </div>
                <div>
                  <p className="text-xs text-stone-500 uppercase">Category</p>
                  <p className="font-medium">{selectedApp.product_category}</p>
                </div>
              </div>

              {selectedApp.website && (
                <div>
                  <p className="text-xs text-stone-500 uppercase">Website</p>
                  <a href={selectedApp.website} target="_blank" rel="noopener noreferrer" 
                    className="text-amber-600 hover:underline flex items-center gap-1">
                    <Globe className="w-4 h-4" /> {selectedApp.website}
                  </a>
                </div>
              )}

              <div className="p-4 bg-stone-50 rounded-lg">
                <p className="text-xs text-stone-500 uppercase mb-2">Message</p>
                <p className="text-stone-700 whitespace-pre-wrap">{selectedApp.message}</p>
              </div>

              <div>
                <Label>Admin Notes</Label>
                <Textarea
                  placeholder="Add internal notes..."
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => handleStatusUpdate("contacted")} disabled={updating}>
                <Mail className="w-4 h-4 mr-1" /> Mark Contacted
              </Button>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="text-red-600" onClick={() => handleStatusUpdate("rejected")} disabled={updating}>
                <XCircle className="w-4 h-4 mr-1" /> Reject
              </Button>
              <Button className="bg-green-600 hover:bg-green-700" onClick={() => handleStatusUpdate("approved")} disabled={updating}>
                <CheckCircle className="w-4 h-4 mr-1" /> Approve
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPartnershipsTab;
